import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApiService from '../../../api/ApiService';

const quoteApiService = new ApiService();

export const extractQuotePoints = response => {
  const data = response?.data || response || {};
  if (data.quote_available === false) return null;

  const rawPoints = data.estimated_points
    ?? data.price
    ?? data.breakdown?.total_cents
    ?? (data.currency === 'points' || data.points_currency === 'points'
      ? data.estimated_price
      : null);
  if (rawPoints === null || rawPoints === undefined || rawPoints === '') return null;
  const numericPoints = Number(rawPoints);
  if (!Number.isFinite(numericPoints) || numericPoints < 0) return null;
  return Math.ceil(numericPoints);
};

const quoteErrorMessage = error => {
  const detail = error?.data?.detail || error?.data?.message;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof error?.message === 'string' && error.message.trim()) return error.message;
  return '暂时无法获取预扣积分';
};

export const useTaskQuote = ({
  enabled,
  apiVersion = 'v3',
  model,
  appName,
  taskType,
  params,
  debounceMs = 350,
}) => {
  const [quoteState, setQuoteState] = useState({
    status: 'idle',
    points: null,
    billingMode: 'prepaid',
    error: null,
    data: null,
  });
  const requestSequence = useRef(0);
  const paramsKey = useMemo(() => JSON.stringify(params || {}), [params]);

  const quoteNow = useCallback(async () => {
    if (!enabled) {
      throw new Error('请先完善必填内容');
    }

    const sequence = ++requestSequence.current;
    setQuoteState(previous => ({ ...previous, status: 'loading', error: null }));

    try {
      const response = apiVersion === 'v2'
        ? await quoteApiService.estimateV2Task(appName, params, taskType)
        : await quoteApiService.quoteV3Task(model, params);
      const points = extractQuotePoints(response);
      if (points === null) {
        throw new Error(response?.data?.description || '报价结果缺少积分字段');
      }
      const data = response?.data || response || {};
      const result = {
        response,
        points,
        billingMode: data.billing_mode || 'prepaid',
        data,
      };
      if (sequence === requestSequence.current) {
        setQuoteState({
          status: 'success',
          points,
          billingMode: result.billingMode,
          error: null,
          data,
        });
      }
      return result;
    } catch (error) {
      if (sequence === requestSequence.current) {
        setQuoteState({
          status: 'error',
          points: null,
          billingMode: 'prepaid',
          error: quoteErrorMessage(error),
          data: null,
        });
      }
      throw error;
    }
  }, [apiVersion, appName, enabled, model, paramsKey, taskType]);

  useEffect(() => {
    if (!enabled) {
      requestSequence.current += 1;
      setQuoteState({
        status: 'idle',
        points: null,
        billingMode: 'prepaid',
        error: null,
        data: null,
      });
      return undefined;
    }

    const timer = setTimeout(() => {
      quoteNow().catch(() => {});
    }, debounceMs);
    return () => {
      clearTimeout(timer);
      requestSequence.current += 1;
    };
  }, [debounceMs, enabled, quoteNow]);

  return { ...quoteState, quoteNow };
};
